import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import i18next from '../../locales/i18n';

@customElement('language-switcher')
export class LanguageSwitcher extends LitElement {
  static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .language-switcher {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.9rem;
      font-weight: 400;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    button {
      background: none;
      border: none;
      color: #374151;
      cursor: pointer;
      padding: 0.5rem 0.75rem;
      min-width: 44px;
      min-height: 44px;
      font-size: inherit;
      font-family: inherit;
      font-weight: inherit;
      text-transform: inherit;
      letter-spacing: inherit;
      transition: all 0.25s ease;
      border-radius: 4px;
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    button:hover {
      color: #00bcd4;
      background-color: rgba(0, 188, 212, 0.1);
    }

    button:focus-visible {
      outline: 3px solid #00bcd4;
      outline-offset: 2px;
    }

    button.active {
      font-weight: 600;
      color: #00bcd4;
    }

    button.active::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0.5rem;
      right: 0.5rem;
      height: 2px;
      background: #00bcd4;
    }

    .separator {
      color: rgba(255, 255, 255, 0.8);
      user-select: none;
    }

    /* When header is scrolled (passed from parent) */
    :host(.scrolled) button {
      color: #374151;
    }

    :host(.scrolled) button:hover {
      color: #00bcd4;
    }

    :host(.scrolled) button.active {
      color: #00bcd4;
      font-weight: 600;
    }

    :host(.scrolled) .separator {
      color: #64748b;
    }
  `;

  @state()
  private currentLang = i18next.language || 'pl';

  connectedCallback() {
    super.connectedCallback();
    // Listen for language changes from other components
    i18next.on('languageChanged', this.handleLanguageChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    i18next.off('languageChanged', this.handleLanguageChange);
  }

  private handleLanguageChange = (lng: string) => {
    this.currentLang = lng;
  };

  private switchLanguage(lang: 'pl' | 'en') {
    if (this.currentLang !== lang) {
      i18next.changeLanguage(lang);
      this.currentLang = lang;

      // Dispatch custom event for other components to react
      this.dispatchEvent(new CustomEvent('language-changed', {
        detail: { language: lang },
        bubbles: true,
        composed: true
      }));
    }
  }

  render() {
    return html`
      <div class="language-switcher" role="group" aria-label="${i18next.t('aria.languageSwitch')}">
        <button
          class="${this.currentLang === 'pl' ? 'active' : ''}"
          @click="${() => this.switchLanguage('pl')}"
          aria-label="${this.currentLang === 'pl' ? i18next.t('aria.switchToPolish') : i18next.t('aria.switchToPolish')}"
          aria-current="${this.currentLang === 'pl' ? 'true' : 'false'}"
        >
          PL
        </button>
        <span class="separator" aria-hidden="true">|</span>
        <button
          class="${this.currentLang === 'en' ? 'active' : ''}"
          @click="${() => this.switchLanguage('en')}"
          aria-label="${this.currentLang === 'en' ? i18next.t('aria.switchToEnglish') : i18next.t('aria.switchToEnglish')}"
          aria-current="${this.currentLang === 'en' ? 'true' : 'false'}"
        >
          EN
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'language-switcher': LanguageSwitcher;
  }
}
